import React, { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { api } from "../lib/api";
import { PageShell } from "../components/PageShell";
import { formatApiError, useAuth } from "../context/AuthContext";

const COLORS = ["#00F0FF", "#B026FF", "#FFD700"];
const money = (v) => v.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 });
const DEFAULT = { name: "Current path", income: 60000, expenses: 3500, savingsRate: 20, contribution: 200, returnPct: 6, inflation: 2.5, years: 20, changeYear: 0, newIncome: 0 };

export const simulate = (p) => {
  const i = p.returnPct / 100 / 12;
  let bal = 0, contributed = 0;
  const rows = [{ year: 0, value: 0, real: 0 }];
  for (let m = 1; m <= p.years * 12; m++) {
    const year = Math.ceil(m / 12);
    const income = p.changeYear && year > p.changeYear ? p.newIncome : p.income;
    const surplus = Math.max(0, income / 12 - p.expenses);
    const add = surplus * (p.savingsRate / 100) + p.contribution;
    bal = bal * (1 + i) + add;
    contributed += add;
    if (m % 12 === 0) rows.push({ year, value: Math.round(bal), real: Math.round(bal / Math.pow(1 + p.inflation / 100, year)), contributed: Math.round(contributed) });
  }
  return rows;
};

export const weeklyFV = (w, r, t) => (r === 0 ? w * 52 * t : w * ((Math.pow(1 + r / 52, 52 * t) - 1) / (r / 52)));

export default function Lab() {
  const { id } = useParams();
  const [tab, setTab] = useState("lab");
  return (
    <PageShell back={`/journey/${id}`} label="Quantum Probability Lab" wide title={<>The <span className="text-glow-cyan">Possibility</span> Lab</>}
      subtitle="Test real-world scenarios with real formulas. Every assumption is shown. Money can be calculated; happiness, love and success can't, so we never invent odds for them.">
      <div className="flex gap-2 mb-6 flex-wrap">{[["lab", "Probability Lab"], ["butterfly", "Butterfly Effect"], ["regret", "Regret Reversal"]].map(([k, l]) =>
        <button key={k} onClick={() => setTab(k)} className={`btn-pill text-xs ${tab === k ? "btn-pill-primary" : ""}`} data-testid={`lab-tab-${k}`}>{l}</button>)}</div>
      {tab === "lab" && <ProbabilityLab />}
      {tab === "butterfly" && <Butterfly id={id} />}
      {tab === "regret" && <Regret id={id} />}
    </PageShell>
  );
}

const Num = ({ label, value, onChange, testId, step = 1 }) => (
  <label className="block"><span className="font-mono-label block mb-1">{label}</span>
    <input type="number" step={step} value={value} onChange={(e) => onChange(Number(e.target.value))} data-testid={testId}
      className="w-full bg-white/[0.03] border border-white/10 rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-cyan-400/60" /></label>
);

const ProbabilityLab = () => {
  const { user } = useAuth();
  const [paths, setPaths] = useState([DEFAULT]);
  const [active, setActive] = useState(0);
  const p = paths[active];
  const set = (k) => (v) => setPaths(paths.map((x, i) => (i === active ? { ...x, [k]: v } : x)));
  const results = useMemo(() => paths.map(simulate), [paths]);
  const years = Math.max(...paths.map((x) => x.years));
  const chart = Array.from({ length: years + 1 }, (_, y) => ({ year: y, ...Object.fromEntries(results.map((r, i) => [paths[i].name, r[y]?.value])) }));
  const add = () => {
    if (!user?.is_premium) return toast.info("Comparing multiple financial paths is a Premium feature.");
    if (paths.length < 3) { setPaths([...paths, { ...p, name: `Path ${paths.length + 1}` }]); setActive(paths.length); }
  };
  return (
    <div className="grid lg:grid-cols-3 gap-5">
      <div className="glass rounded-3xl p-5 space-y-3" data-testid="lab-inputs">
        <div className="flex gap-2 flex-wrap">{paths.map((x, i) => <button key={i} onClick={() => setActive(i)} className={`btn-pill text-xs ${i === active ? "btn-pill-primary" : ""}`} style={{ color: COLORS[i] }} data-testid={`lab-path-${i}`}>{x.name}</button>)}
          {paths.length < 3 && <button onClick={add} className="btn-pill text-xs" data-testid="lab-add-path"><Plus size={12} /> Path</button>}
          {paths.length > 1 && <button onClick={() => { setPaths(paths.filter((_, i) => i !== active)); setActive(0); }} className="btn-pill text-xs" aria-label="Remove path"><Trash2 size={12} /></button>}</div>
        <label className="block"><span className="font-mono-label block mb-1">Path name</span><input value={p.name} onChange={(e) => set("name")(e.target.value)} className="w-full bg-white/[0.03] border border-white/10 rounded-xl px-3 py-2 text-sm text-white" data-testid="lab-name" /></label>
        <div className="grid grid-cols-2 gap-3">
          <Num label="Annual income $" value={p.income} onChange={set("income")} testId="lab-income" step={1000} />
          <Num label="Monthly expenses $" value={p.expenses} onChange={set("expenses")} testId="lab-expenses" step={100} />
          <Num label="Savings rate % of surplus" value={p.savingsRate} onChange={set("savingsRate")} testId="lab-savings" />
          <Num label="Extra monthly invest $" value={p.contribution} onChange={set("contribution")} testId="lab-contribution" step={50} />
          <Num label="Annual return %" value={p.returnPct} onChange={set("returnPct")} testId="lab-return" step={0.5} />
          <Num label="Inflation %" value={p.inflation} onChange={set("inflation")} testId="lab-inflation" step={0.5} />
          <Num label="Time horizon (yrs)" value={p.years} onChange={(v) => set("years")(Math.min(Math.max(v, 1), 50))} testId="lab-years" />
          <Num label="Career change after yr (0 = none)" value={p.changeYear} onChange={set("changeYear")} testId="lab-change-year" />
          <Num label="New income after change $" value={p.newIncome} onChange={set("newIncome")} testId="lab-new-income" step={1000} />
        </div>
      </div>
      <div className="lg:col-span-2 space-y-5">
        <div className="glass rounded-3xl p-5" data-testid="lab-chart">
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chart}><CartesianGrid stroke="rgba(255,255,255,0.06)" /><XAxis dataKey="year" stroke="#888" /><YAxis stroke="#888" tickFormatter={(v) => `$${Math.round(v / 1000)}k`} />
              <Tooltip formatter={(v) => money(v)} contentStyle={{ background: "#0A051A", border: "1px solid #333" }} /><Legend />
              {paths.map((x, i) => <Line key={i} type="monotone" dataKey={x.name} stroke={COLORS[i]} strokeWidth={2} dot={false} />)}</LineChart>
          </ResponsiveContainer>
          <div className="grid md:grid-cols-3 gap-3 mt-4">{results.map((r, i) => { const last = r[r.length - 1]; return (
            <div key={i} className="rounded-2xl border border-white/10 p-3 text-xs" data-testid={`lab-result-${i}`}><div style={{ color: COLORS[i] }} className="font-display text-sm mb-1">{paths[i].name}</div>
              <div>Balance: {money(last.value)}</div><div>In today's dollars: {money(last.real)}</div><div>You contribute: {money(last.contributed || 0)}</div><div>Growth: {money(last.value - (last.contributed || 0))}</div></div>); })}</div>
        </div>
        <div className="glass rounded-3xl p-5 text-xs text-white/60 leading-relaxed" data-testid="lab-assumptions">
          <div className="font-mono-label mb-2">Assumptions & formulas</div>
          Monthly surplus = income ÷ 12 − monthly expenses (never below $0). Monthly investment = surplus × savings rate + extra monthly investment.
          Balance grows monthly: B(m) = B(m−1) × (1 + r/12) + investment, where r is the annual return. After a career change, the new income applies from the following year.
          Today's dollars = balance ÷ (1 + inflation)^years. Returns are assumed constant. There are no taxes, fees or market swings, and nothing here is guaranteed or financial advice.
        </div>
      </div>
    </div>
  );
};

const Butterfly = ({ id }) => {
  const [weekly, setWeekly] = useState(25);
  const [rate, setRate] = useState(6);
  const [change, setChange] = useState("What if I saved $25 every week?");
  const [stories, setStories] = useState(null);
  const [busy, setBusy] = useState(false);
  const pts = [1, 5, 10, 20].map((t) => ({ t, fv: weeklyFV(weekly, rate / 100, t), put: weekly * 52 * t }));
  const explore = async () => {
    setBusy(true);
    try { setStories((await api.post(`/journey/${id}/butterfly`, { message: change })).data.horizons); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-5">
      <div className="glass rounded-3xl p-5 grid md:grid-cols-4 gap-3 items-end">
        <label className="md:col-span-2 block"><span className="font-mono-label block mb-1">One small change</span>
          <input value={change} onChange={(e) => setChange(e.target.value)} data-testid="bfly-change" className="w-full bg-white/[0.03] border border-white/10 rounded-xl px-3 py-2 text-sm text-white" /></label>
        <Num label="Weekly amount $" value={weekly} onChange={setWeekly} testId="bfly-weekly" />
        <Num label="Annual return %" value={rate} onChange={setRate} testId="bfly-rate" step={0.5} />
      </div>
      <div className="glass rounded-3xl p-6" data-testid="bfly-timeline">
        <div className="relative flex justify-between">
          <div className="absolute left-0 right-0 top-4 h-px bg-gradient-to-r from-cyan-400 via-purple-500 to-amber-300" />
          {pts.map((x, i) => (
            <motion.div key={x.t} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.15 }} className="relative text-center w-1/4 px-1" data-testid={`bfly-point-${x.t}`}>
              <span className="block w-8 h-8 mx-auto rounded-full border-2 border-amber-300 bg-[#05050A] shadow-[0_0_14px_rgba(255,215,0,0.5)]" />
              <div className="font-mono-label mt-2">{x.t} yr{x.t > 1 ? "s" : ""}</div>
              <div className="font-display text-lg md:text-2xl">{money(x.fv)}</div>
              <div className="text-[10px] text-white/45">you put in {money(x.put)}</div>
              {stories?.find((s) => Number(s.years) === x.t) && <p className="text-[11px] text-white/65 mt-2 italic">{stories.find((s) => Number(s.years) === x.t).story}</p>}
            </motion.div>))}
        </div>
        <p className="text-[11px] text-white/45 mt-6">Formula: FV = W × ((1 + r/52)^(52t) − 1) ÷ (r/52), with weekly deposits W, annual return r (assumed constant) and t years. Before taxes, fees and inflation.</p>
        <button onClick={explore} disabled={busy} className="btn-pill btn-pill-primary text-xs mt-4" data-testid="bfly-explore">{busy ? <Loader2 size={12} className="animate-spin" /> : null} Imagine the ripple effects on the rest of life</button>
        {stories && <p className="text-[10px] text-white/35 mt-2">The ripple stories are imaginative possibilities, not predictions or probabilities.</p>}
      </div>
    </div>
  );
};

const Regret = ({ id }) => {
  const [text, setText] = useState("I wish I had started investing at age 21.");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try { setRes((await api.post(`/journey/${id}/regret`, { message: text })).data); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const save = async () => {
    try { await api.post(`/journey/${id}/journal`, { type: "regret", title: `Regret reversal: ${text}`, content: res }); toast.success("Saved to your journal."); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="glass rounded-3xl p-6 max-w-3xl" data-testid="regret-panel">
      <div className="font-mono-label mb-2">Quantum Regret Reversal</div>
      <textarea rows={2} value={text} onChange={(e) => setText(e.target.value)} data-testid="regret-input" className="w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3 text-sm text-white" />
      <button onClick={run} disabled={busy} className="btn-pill btn-pill-primary text-xs mt-3" data-testid="regret-run">{busy ? <Loader2 size={12} className="animate-spin" /> : null} Revisit this decision</button>
      {res && <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-6 space-y-4" data-testid="regret-result">
        <p className="text-sm italic text-white/80 leading-relaxed">{res.scenario}</p>
        <p className="font-display text-xl text-amber-200">What can you still change today?</p>
        <ol className="space-y-2">{(res.steps || []).map((s, i) => <li key={i} className="flex gap-3 text-sm"><span className="text-cyan-300">{i + 1}.</span>{s}</li>)}</ol>
        <p className="text-sm text-white/60">{res.reframe}</p>
        <button onClick={save} className="btn-pill text-xs" data-testid="regret-save">Save to journal</button>
      </motion.div>}
    </div>
  );
};
